import { useEffect, useRef } from "react";

interface UseTabChangeProps {
  onTabChange: () => void;
  enabled?: boolean;
  debounceDelay?: number; // Default: 2000ms
}

// Track browser screen-share prompt
let isRequestingScreenShare = false;

// Intercept screen-sharing to avoid focus warnings
if (
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  navigator.mediaDevices?.getDisplayMedia
) {
  const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia;
  navigator.mediaDevices.getDisplayMedia = async function (options) {
    isRequestingScreenShare = true;
    try {
      return await originalGetDisplayMedia.call(
        navigator.mediaDevices,
        options,
      );
    } finally {
      // Grace period for focus shift
      setTimeout(() => {
        isRequestingScreenShare = false;
      }, 2000);
    }
  };
}

/**
 * Monitors tab/window visibility and focus.
 */
export function useTabChange({
  onTabChange,
  enabled = true,
  debounceDelay = 2000,
}: UseTabChangeProps) {
  const lastTabChangeTime = useRef<number>(0);
  const onTabChangeRef = useRef<() => void>(onTabChange);

  // Sync latest callback
  useEffect(() => {
    onTabChangeRef.current = onTabChange;
  }, [onTabChange]);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityOrBlur = () => {
      // Skip if screenshare prompt is active
      if (isRequestingScreenShare) return;

      // Prevent duplicate events
      const now = Date.now();
      if (now - lastTabChangeTime.current < debounceDelay) return;

      lastTabChangeTime.current = now;
      onTabChangeRef.current();
    };

    // Tab visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) handleVisibilityOrBlur();
    };

    // Window focus changes
    const handleBlur = () => {
      handleVisibilityOrBlur();
    };

    // Register listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    // Cleanup listeners
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [enabled, debounceDelay]);
}


